import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { bookingsAPI } from '../api/bookings';
import { providersAPI } from '../api/providers';
import { paymentsAPI } from '../api/payments';
import { useAuth } from '../hooks/useAuth';
import Spinner from '../components/common/Spinner';
import Button from '../components/common/Button';
import Modal from '../components/common/Modal';
import { CalendarIcon, ClockIcon, StarIcon, XMarkIcon, ArrowPathIcon, CreditCardIcon } from '@heroicons/react/24/outline';
import { CheckBadgeIcon } from '@heroicons/react/24/solid';
import { formatCurrency } from '../utils/formatter';
import toast from 'react-hot-toast';

const Bookings = () => {
  const navigate = useNavigate();
  const { user, isProvider } = useAuth();
  const [bookings, setBookings] = useState([]);
  const [filteredBookings, setFilteredBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [showRescheduleModal, setShowRescheduleModal] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [cancelReason, setCancelReason] = useState('');
  const [rescheduleDate, setRescheduleDate] = useState('');
  const [rescheduleReason, setRescheduleReason] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  const [showPayModal, setShowPayModal] = useState(false);
  const [payingBooking, setPayingBooking] = useState(null);
  const [phoneNumber, setPhoneNumber] = useState('');
  const [payLoading, setPayLoading] = useState(false);
  const [rescheduleError, setRescheduleError] = useState('');
  const [payError, setPayError] = useState('');

  const [providerProfiles, setProviderProfiles] = useState({});
  const [serviceNames, setServiceNames] = useState({});
  const [reviewedProviderIds, setReviewedProviderIds] = useState(() => new Set());
  const [reviewedBookingIds, setReviewedBookingIds] = useState(() => new Set());

  // Providers section state (for clients)
  const [activeTab, setActiveTab] = useState('bookings');
  const [providers, setProviders] = useState([]);
  const [providersLoading, setProvidersLoading] = useState(false);
  const [showProviderFilters, setShowProviderFilters] = useState(false);
  const [providerFilters, setProviderFilters] = useState({
    category: '',
    location: '',
    minPrice: '',
    maxPrice: '',
    rating: '',
    nearMe: false,
  });

  useEffect(() => {
    fetchBookings();
  }, []);

  useEffect(() => {
    filterBookings();
  }, [statusFilter, bookings]);

  // Fetch providers when tab changes to 'providers'
  useEffect(() => {
    if (activeTab === 'providers' && !isProvider) {
      fetchProviders();
    }
  }, [activeTab]);

  // Fetch providers when filters change
  useEffect(() => {
    if (activeTab === 'providers' && !isProvider) {
      fetchProviders();
    }
  }, [providerFilters]);

  const fetchProviders = async () => {
    setProvidersLoading(true);
    try {
      const params = {};
      if (providerFilters.category) params.category = providerFilters.category;
      if (providerFilters.location) params.location = providerFilters.location;
      if (providerFilters.minPrice) params.minPrice = providerFilters.minPrice;
      if (providerFilters.maxPrice) params.maxPrice = providerFilters.maxPrice;
      if (providerFilters.rating) params.rating = providerFilters.rating;

      const data = await providersAPI.searchProviders(params);
      let fetchedProviders = data.providers || data;

      // Filter by near me if enabled
      if (providerFilters.nearMe && user?.location) {
        fetchedProviders = fetchedProviders.filter(
          (p) => p.location?.toLowerCase() === user.location.toLowerCase()
        );
      }

      setProviders(fetchedProviders);
    } catch (error) {
      console.error('Error fetching providers:', error);
      toast.error('Failed to load providers');
    } finally {
      setProvidersLoading(false);
    }
  };

  const handleProviderFilterChange = (e) => {
    const { name, value, type, checked } = e.target;
    setProviderFilters({
      ...providerFilters,
      [name]: type === 'checkbox' ? checked : value,
    });
  };

  const clearProviderFilters = () => {
    setProviderFilters({
      category: '',
      location: '',
      minPrice: '',
      maxPrice: '',
      rating: '',
      nearMe: false,
    });
  };

  const fetchBookings = async () => {
    setLoading(true);
    try {
      const params = isProvider
        ? { providerId: user.id }
        : { clientId: user.id };

      const [data, reviewsData] = await Promise.all([
        bookingsAPI.getBookings(params, { silent: true }),
        !isProvider
          ? providersAPI.getMyReviews().catch(() => ({ reviews: [] }))
          : Promise.resolve({ reviews: [] }),
      ]);
      const bookingsList = Array.isArray(data) ? data : data.bookings || [];
      const myReviews = reviewsData?.reviews || [];
      setReviewedProviderIds(new Set(myReviews.map((review) => String(review.providerId)).filter(Boolean)));
      setReviewedBookingIds(new Set(myReviews.map((review) => String(review.bookingId)).filter(Boolean)));

      // Check for confirmed bookings that might already be paid
      if (!isProvider) {
        try {
          const payments = await paymentsAPI.getPaymentHistory();
          const paidPayments = (Array.isArray(payments) ? payments : []).filter(p => p.status === 'COMPLETED');
          const paidBookingIds = new Set(paidPayments.map(p => p.bookingId));
          bookingsList.forEach(b => {
            if (b.status === 'CONFIRMED' && paidBookingIds.has(b.id)) {
              b.status = 'PAID';
            }
          });
        } catch {}
      }

      setBookings(bookingsList);

      // Fetch provider profiles and service names
      const uniqueProviderIds = [...new Set(bookingsList.map(b => b.providerId).filter(Boolean))];
      const profiles = {};
      const svcMap = {};
      await Promise.all(uniqueProviderIds.map(async (pid) => {
        try {
          const p = await providersAPI.getProvider(pid);
          profiles[pid] = p;
          // Fetch services for this provider to get service names
          const svcData = await providersAPI.getProviderServices(pid);
          const servicesList = svcData?.services || (Array.isArray(svcData) ? svcData : []);
          servicesList.forEach(s => { svcMap[s.id] = s.name; });
        } catch {}
      }));
      setProviderProfiles(profiles);
      setServiceNames(svcMap);
    } catch (error) {
      console.error('Error fetching bookings:', error);
    } finally {
      setLoading(false);
    }
  };

  const filterBookings = () => {
    if (statusFilter === 'all') {
      setFilteredBookings(bookings);
    } else if (statusFilter === 'upcoming') {
      setFilteredBookings(
        bookings.filter((b) =>
          ['PENDING', 'ACCEPTED', 'PAID'].includes(b.status)
        )
      );
    } else if (statusFilter === 'past') {
      setFilteredBookings(
        bookings.filter((b) =>
          ['COMPLETED', 'CANCELLED', 'REFUNDED', 'NO_SHOW'].includes(b.status)
        )
      );
    } else {
      setFilteredBookings(bookings.filter((b) => b.status === statusFilter));
    }
  };

  const handleAction = async (action, bookingOrId) => {
    const bookingId = typeof bookingOrId === 'string' ? bookingOrId : bookingOrId?.id;
    const booking = typeof bookingOrId === 'object' ? bookingOrId : bookings.find((b) => b.id === bookingId);

    switch (action) {
      case 'pay':
        setPayingBooking(booking);
        setShowPayModal(true);
        break;
      case 'confirm':
        await handleConfirm(bookingId);
        break;
      case 'decline':
        setSelectedBooking(booking);
        setShowCancelModal(true);
        break;
      case 'cancel':
        setSelectedBooking(booking);
        setShowCancelModal(true);
        break;
      case 'reschedule':
        setSelectedBooking(booking);
        setShowRescheduleModal(true);
        break;
      case 'complete':
        await handleComplete(bookingId);
        break;
      case 'review':
        navigate('/my-reviews');
        break;
      case 'viewReview':
        navigate('/my-reviews?filter=Reviewed');
        break;
      default:
        break;
    }
  };

  const handleConfirm = async (bookingId) => {
    try {
      await bookingsAPI.confirmBooking(bookingId);
      toast.success('Booking confirmed!');
      fetchBookings();
    } catch (error) {
      console.error('Confirm error:', error);
      toast.error('Failed to confirm booking');
    }
  };

  const handleComplete = async (bookingId) => {
    try {
      await bookingsAPI.completeBooking(bookingId);
      toast.success('Booking marked as complete!');
      fetchBookings();
    } catch (error) {
      console.error('Complete error:', error);
      toast.error('Failed to complete booking');
    }
  };

  const handleCancelSubmit = async () => {
    if (!selectedBooking) return;

    setActionLoading(true);
    try {
      if (isProvider) {
        await bookingsAPI.declineBooking(selectedBooking.id, cancelReason);
        toast.success('Booking declined');
      } else {
        await bookingsAPI.cancelBooking(selectedBooking.id, {
          cancelledBy: user.id,
          reason: cancelReason,
        });
        toast.success('Booking cancelled');
      }
      setShowCancelModal(false);
      setCancelReason('');
      fetchBookings();
    } catch (error) {
      console.error('Cancel error:', error);
      toast.error('Failed to cancel booking');
    } finally {
      setActionLoading(false);
    }
  };

  const handleRescheduleSubmit = async () => {
    if (!selectedBooking || !rescheduleDate) {
      setRescheduleError('Please select a new date and time');
      return;
    }

    setActionLoading(true);
    try {
      await bookingsAPI.rescheduleBooking(selectedBooking.id, {
        newStartAt: rescheduleDate,
        requestedBy: user.id,
        reason: rescheduleReason,
      });
      toast.success('Booking rescheduled successfully!');
      setShowRescheduleModal(false);
      setRescheduleDate('');
      setRescheduleReason('');
      setRescheduleError('');
      fetchBookings();
    } catch (error) {
      console.error('Reschedule error:', error);
      toast.error(error.response?.data?.error || 'Failed to reschedule booking');
    } finally {
      setActionLoading(false);
    }
  };

  const minDateTime = new Date();
  minDateTime.setHours(minDateTime.getHours() + 2);
  const minDateTimeString = minDateTime.toISOString().slice(0, 16);

  // Group bookings by status
  const [searchQuery, setSearchQuery] = useState('');
  const [activeFilter, setActiveFilter] = useState('All');
  const [calendarMonth, setCalendarMonth] = useState(new Date());

  const confirmedBookings = bookings.filter(b => b.status === 'CONFIRMED');
  const paidBookings = bookings.filter(b => b.status === 'PAID');
  const pendingBookings = bookings.filter(b => b.status === 'PENDING');
  const completedBookings = bookings.filter(b => b.status === 'COMPLETED');
  const otherBookings = bookings.filter(b => !['CONFIRMED', 'PAID', 'PENDING', 'COMPLETED'].includes(b.status));

  // Filtered bookings based on active filter and search
  const displayBookings = bookings.filter(b => {
    if (activeFilter.startsWith('DATE:')) {
      const filterDate = activeFilter.replace('DATE:', '');
      const bookingDate = new Date(b.startAt).toISOString().split('T')[0];
      if (bookingDate !== filterDate) return false;
    } else if (activeFilter !== 'All' && b.status !== activeFilter) {
      return false;
    }
    if (searchQuery && !(b.service?.name || '').toLowerCase().includes(searchQuery.toLowerCase())) return false;
    return true;
  });

  // Calendar helpers
  const calendarDays = () => {
    const y = calendarMonth.getFullYear();
    const m = calendarMonth.getMonth();
    const firstDay = new Date(y, m, 1).getDay();
    const daysInMonth = new Date(y, m + 1, 0).getDate();
    const days = [];
    for (let i = 0; i < firstDay; i++) days.push(null);
    for (let d = 1; d <= daysInMonth; d++) days.push(d);
    return days;
  };

  const bookingDates = new Set(bookings.map(b => {
    const d = new Date(b.startAt);
    return `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
  }));

  const isBookingDay = (day) => {
    if (!day) return false;
    const key = `${calendarMonth.getFullYear()}-${calendarMonth.getMonth()}-${day}`;
    return bookingDates.has(key);
  };

  const isToday = (day) => {
    if (!day) return false;
    const now = new Date();
    return day === now.getDate() && calendarMonth.getMonth() === now.getMonth() && calendarMonth.getFullYear() === now.getFullYear();
  };

  const statusColors = {
    PENDING: '#f97316', CONFIRMED: '#3b82f6', PAID: '#22c55e',
    COMPLETED: '#22c55e', CANCELLED: '#ef4444', DECLINED: '#ef4444', NO_SHOW: '#ef4444',
  };

  const statusLabels = {
    PENDING: 'Pending', CONFIRMED: 'Confirmed', PAID: 'Paid',
    COMPLETED: 'Completed', CANCELLED: 'Cancelled', DECLINED: 'Declined', NO_SHOW: 'No Show',
  };

  const hasReviewForBooking = (booking) =>
    reviewedBookingIds.has(String(booking.id)) ||
    reviewedProviderIds.has(String(booking.providerId));


  return (
    <div className="min-h-screen" style={{ backgroundColor: 'var(--color-background)' }}>
      <div className="px-4 py-6 sm:px-6 sm:py-8 lg:px-8">

        {/* Top bar — search + filters + action, full width */}
        <div className="mb-0 flex flex-col gap-3 sm:flex-row sm:items-center" style={{ paddingBottom: '16px', borderBottom: '1px solid var(--gray-4)' }}>
          {/* Search */}
          <div className="relative w-full flex-1">
            <svg style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', width: '16px', height: '16px', color: 'var(--gray-9)' }} fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><circle cx="11" cy="11" r="8"/><path d="M21 21l-4.35-4.35"/></svg>
            <input
              type="text"
              placeholder="Search bookings..."
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              style={{ width: '100%', padding: '8px 12px 8px 36px', borderRadius: '8px', border: '1px solid var(--gray-5)', backgroundColor: 'var(--color-background)', color: 'var(--gray-12)', fontSize: '14px', outline: 'none' }}
            />
          </div>

          <div className="flex w-full flex-wrap items-center gap-2 sm:w-auto sm:flex-nowrap sm:justify-end">
            {/* Date filter */}
            <div className="relative">
              <input
                type="date"
                onChange={e => {
                  if (e.target.value) {
                    setActiveFilter('DATE:' + e.target.value);
                  } else {
                    setActiveFilter('All');
                  }
                }}
                style={{ position: 'absolute', opacity: 0, width: '34px', height: '34px', cursor: 'pointer' }}
              />
              <button
                title="Filter by date"
                style={{
                  width: '34px', height: '34px', borderRadius: '8px', border: 'none',
                  cursor: 'pointer', transition: 'all 0.15s',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  backgroundColor: activeFilter.startsWith('DATE:') ? 'var(--orange-9)' : 'transparent',
                  color: activeFilter.startsWith('DATE:') ? '#fff' : 'var(--gray-9)',
                }}
                onMouseEnter={e => { if (!activeFilter.startsWith('DATE:')) { e.currentTarget.style.backgroundColor = 'var(--gray-3)'; } }}
                onMouseLeave={e => { if (!activeFilter.startsWith('DATE:')) { e.currentTarget.style.backgroundColor = 'transparent'; } }}
              >
                <CalendarIcon style={{ width: '16px', height: '16px' }} />
              </button>
            </div>

            {/* Filters — icons only, tooltip on hover */}
            <div className="flex min-w-0 flex-1 flex-wrap gap-1 sm:flex-initial sm:flex-nowrap">
              {[
                { key: 'All', label: 'All', icon: <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M4 6h16M4 12h16M4 18h16"/></svg> },
                { key: 'PENDING', label: 'Pending', icon: <ClockIcon style={{ width: '16px', height: '16px' }} /> },
                { key: 'CONFIRMED', label: 'Confirmed', icon: <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><rect x="2" y="5" width="20" height="14" rx="2"/><path d="M2 10h20"/></svg> },
                { key: 'PAID', label: 'Paid', icon: <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M12 1v22M17 5H9.5a3.5 3.5 0 000 7h5a3.5 3.5 0 010 7H6"/></svg> },
                { key: 'COMPLETED', label: 'Completed', icon: <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24"><path d="M20 6L9 17l-5-5"/></svg> },
              ].map(({ key, label, icon }) => (
                <button
                  key={key}
                  onClick={() => setActiveFilter(key)}
                  title={label}
                  style={{
                    width: '34px', height: '34px', borderRadius: '8px', border: 'none',
                    cursor: 'pointer', transition: 'all 0.15s',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    backgroundColor: activeFilter === key ? 'var(--orange-9)' : 'transparent',
                    color: activeFilter === key ? '#fff' : 'var(--gray-9)',
                  }}
                  onMouseEnter={e => { if (activeFilter !== key) { e.currentTarget.style.backgroundColor = 'var(--gray-3)'; } }}
                  onMouseLeave={e => { if (activeFilter !== key) { e.currentTarget.style.backgroundColor = 'transparent'; } }}
                >
                  {icon}
                </button>
              ))}
            </div>

            {/* Action */}
            <Button onClick={() => navigate('/search?view=providers')} size="small" className="shrink-0">
              <CalendarIcon style={{ width: '14px', height: '14px' }} />
              <span className="hidden sm:inline">Book Service</span>
              <span className="sm:hidden">Book</span>
            </Button>
          </div>
        </div>

        {/* Bookings list — full width */}
        <div className="mt-4">
          <div>
            {/* Booking list */}
            {loading ? (
              <div className="flex justify-center py-16"><Spinner size="large" /></div>
            ) : displayBookings.length === 0 ? (
              <div className="text-center py-12">
                <CalendarIcon style={{ width: '40px', height: '40px', color: 'var(--gray-7)', margin: '0 auto 8px' }} />
                <p style={{ color: 'var(--gray-10)', fontSize: '14px' }}>
                  {searchQuery || activeFilter !== 'All' ? 'No bookings match your filter' : 'No bookings yet'}
                </p>
              </div>
            ) : (
              <div>
                {displayBookings.map((booking) => {
                  const date = new Date(booking.startAt);
                  const dateStr = date.toLocaleDateString('en', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' });
                  const time = date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
                  const color = statusColors[booking.status] || '#999';
                  const provider = providerProfiles[booking.providerId];
                  const providerName = provider?.businessName || provider?.name || 'Provider';
                  const providerPhoto = provider?.photoUrl;
                  const isVerified = provider?.isApproved;
                  const serviceName = serviceNames[booking.serviceId] || booking.service?.name || 'Booking';

                  return (
                    <div key={booking.id} style={{ borderBottom: '1px solid var(--gray-4)', padding: '16px 0' }}>
                      <div className="flex items-start gap-3">
                        {/* Provider profile pic */}
                        {providerPhoto ? (
                          <img src={providerPhoto} alt={providerName}
                            style={{ width: '38px', height: '38px', borderRadius: '50%', objectFit: 'cover', flexShrink: 0, marginTop: '2px' }} />
                        ) : (
                          <div style={{
                            width: '38px', height: '38px', borderRadius: '50%', flexShrink: 0, marginTop: '2px',
                            backgroundColor: 'var(--orange-3)', display: 'flex', alignItems: 'center', justifyContent: 'center',
                            fontSize: '14px', fontWeight: 700, color: 'var(--orange-9)',
                          }}>
                            {providerName.charAt(0).toUpperCase()}
                          </div>
                        )}

                        <div style={{ flex: 1, minWidth: 0 }}>
                          {/* Row 1: Provider name + right side (status, amount, actions) */}
                          <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
                            <div className="min-w-0">
                              <h4 className="flex items-start gap-1" style={{ fontSize: '14px', fontWeight: 600, color: 'var(--gray-12)', margin: 0 }}>
                                <span className="break-words">{providerName}</span>
                                {isVerified && <CheckBadgeIcon style={{ width: '16px', height: '16px', color: 'var(--orange-9)' }} />}
                              </h4>
                              <span className="block break-all" style={{ fontSize: '11px', color: 'var(--gray-9)' }}>@{providerName.toLowerCase().replace(/\s+/g, '')}</span>
                            </div>
                            <div className="flex flex-wrap items-center gap-2 sm:justify-end" style={{ flexShrink: 0 }}>
                              <span className="whitespace-nowrap" style={{
                                fontSize: '11px', fontWeight: 600, color, textTransform: 'uppercase',
                                backgroundColor: color + '15', padding: '3px 10px', borderRadius: '20px',
                              }}>
                                {statusLabels[booking.status]}
                              </span>
                              <span className="whitespace-nowrap" style={{ fontSize: '14px', fontWeight: 700, color: 'var(--orange-9)', fontFamily: "'Poppins', sans-serif" }}>
                                {formatCurrency(booking.totalPaid || booking.subtotal || 0)}
                              </span>
                              {booking.status === 'CONFIRMED' && !isProvider && (
                                <button onClick={() => handleAction('pay', booking)} className="flex items-center gap-1.5 whitespace-nowrap"
                                  style={{ padding: '5px 14px', borderRadius: '6px', border: 'none', backgroundColor: '#22c55e', color: '#fff', fontSize: '11px', fontWeight: 600, cursor: 'pointer' }}>
                                  <CreditCardIcon style={{ width: '13px', height: '13px' }} />
                                  Pay Now
                                </button>
                              )}
                              {booking.status === 'PENDING' && !isProvider && (
                                <div className="flex flex-wrap items-center gap-2">
                                  <button onClick={() => handleAction('reschedule', booking.id)} className="flex items-center gap-1.5 whitespace-nowrap"
                                    style={{ padding: '4px 12px', borderRadius: '6px', border: '1px solid var(--gray-6)', backgroundColor: 'transparent', color: 'var(--gray-11)', fontSize: '11px', fontWeight: 500, cursor: 'pointer' }}>
                                    <ArrowPathIcon style={{ width: '13px', height: '13px' }} />
                                    Reschedule
                                  </button>
                                  <button onClick={() => handleAction('cancel', booking.id)} className="flex items-center gap-1.5 whitespace-nowrap"
                                    style={{ padding: '4px 12px', borderRadius: '6px', border: '1px solid #ef4444', backgroundColor: 'transparent', color: '#ef4444', fontSize: '11px', fontWeight: 500, cursor: 'pointer' }}>
                                    <XMarkIcon style={{ width: '13px', height: '13px' }} />
                                    Cancel
                                  </button>
                                </div>
                              )}
                              {booking.status === 'COMPLETED' && !isProvider && (
                                hasReviewForBooking(booking) ? (
                                  <button onClick={() => handleAction('viewReview', booking.id)} className="flex items-center gap-1.5 whitespace-nowrap"
                                    style={{ padding: '4px 12px', borderRadius: '6px', border: '1px solid #22c55e55', backgroundColor: '#22c55e12', color: '#16a34a', fontSize: '11px', fontWeight: 600, cursor: 'pointer' }}>
                                    <StarIcon style={{ width: '13px', height: '13px' }} />
                                    View Review
                                  </button>
                                ) : (
                                  <button onClick={() => handleAction('review', booking.id)} className="flex items-center gap-1.5 whitespace-nowrap"
                                    style={{ padding: '4px 12px', borderRadius: '6px', border: '1px solid var(--orange-6)', backgroundColor: 'transparent', color: 'var(--orange-9)', fontSize: '11px', fontWeight: 500, cursor: 'pointer' }}>
                                    <StarIcon style={{ width: '13px', height: '13px' }} />
                                    Leave Review
                                  </button>
                                )
                              )}
                            </div>
                          </div>

                          {/* Row 2: Service name + notes */}
                          <p style={{ fontSize: '13px', color: 'var(--gray-12)', margin: '4px 0 0', fontWeight: 500 }}>
                            {serviceName}
                          </p>
                          {booking.notes && (
                            <p style={{ fontSize: '12px', color: 'var(--gray-10)', margin: '2px 0 0' }}>{booking.notes}</p>
                          )}

                          {/* Row 3: Date + time at bottom */}
                          <div className="flex flex-wrap items-center gap-x-4 gap-y-2" style={{ marginTop: '6px' }}>
                            <span className="flex items-center gap-1.5" style={{ fontSize: '12px', color: 'var(--gray-9)' }}>
                              <CalendarIcon style={{ width: '13px', height: '13px' }} />
                              {dateStr}
                            </span>
                            <span className="flex items-center gap-1.5" style={{ fontSize: '12px', color: 'var(--gray-9)' }}>
                              <ClockIcon style={{ width: '13px', height: '13px' }} />
                              {time}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Showing count */}
            {displayBookings.length > 0 && (
              <p style={{ fontSize: '13px', color: 'var(--gray-9)', marginTop: '16px', textAlign: 'left' }}>
                Showing {displayBookings.length} of {bookings.length} bookings
              </p>
            )}
          </div>

          {/* </div>   */}
        </div>
      </div>

      {/* Cancel Modal */}
      <Modal
        isOpen={showCancelModal}
        onClose={() => setShowCancelModal(false)}
        title={isProvider ? 'Decline Booking' : 'Cancel Booking'}
      >
        <div>
          <p style={{ fontSize: '13px', color: 'var(--gray-10)', margin: '0 0 12px' }}>
            {isProvider
              ? 'Please provide a reason for declining.'
              : 'Cancellation fees may apply. Please provide a reason.'}
          </p>

          <textarea
            value={cancelReason}
            onChange={(e) => setCancelReason(e.target.value)}
            placeholder="Reason for cancellation..."
            rows={3}
            style={{
              width: '100%', padding: '10px 12px', borderRadius: '8px',
              border: '1px solid var(--gray-5)', backgroundColor: 'var(--color-background)',
              color: 'var(--gray-12)', fontSize: '13px', resize: 'vertical',
              outline: 'none', fontFamily: 'inherit',
            }}
          />

          <div className="flex gap-2 justify-end" style={{ marginTop: '14px' }}>
            <button
              onClick={() => setShowCancelModal(false)}
              disabled={actionLoading}
              style={{
                padding: '6px 16px', borderRadius: '6px', fontSize: '12px', fontWeight: 500,
                border: '1px solid var(--gray-5)', backgroundColor: 'transparent',
                color: 'var(--gray-11)', cursor: 'pointer',
              }}
            >
              Keep Booking
            </button>
            <button
              onClick={handleCancelSubmit}
              disabled={actionLoading || !cancelReason.trim()}
              style={{
                padding: '6px 16px', borderRadius: '6px', fontSize: '12px', fontWeight: 600,
                border: 'none', backgroundColor: !cancelReason.trim() ? 'var(--gray-6)' : '#ef4444',
                color: '#fff', cursor: !cancelReason.trim() ? 'not-allowed' : 'pointer',
                opacity: actionLoading ? 0.6 : 1,
              }}
            >
              {actionLoading ? 'Processing...' : (isProvider ? 'Decline' : 'Cancel Booking')}
            </button>
          </div>
        </div>
      </Modal>

      {/* Reschedule Modal */}
      <Modal
        isOpen={showRescheduleModal}
        onClose={() => { setShowRescheduleModal(false); setRescheduleError(''); }}
        title="Reschedule Booking"
      >
        <div>
          <p style={{ fontSize: '13px', color: 'var(--gray-10)', margin: '0 0 12px' }}>
            Pick a new date and time for your appointment.
          </p>

          <label style={{ fontSize: '12px', fontWeight: 500, color: 'var(--gray-11)', display: 'block', marginBottom: '4px' }}>New Date & Time</label>
          <input
            type="datetime-local"
            value={rescheduleDate}
            onChange={(e) => {
              setRescheduleDate(e.target.value);
              setRescheduleError('');
            }}
            min={minDateTimeString}
            style={{
              width: '100%', padding: '8px 12px', borderRadius: '8px',
              border: `1px solid ${rescheduleError ? 'var(--red-9, #e5484d)' : 'var(--gray-5)'}`, backgroundColor: 'var(--color-background)',
              color: 'var(--gray-12)', fontSize: '13px', outline: 'none', fontFamily: 'inherit',
            }}
          />
          {rescheduleError && (
            <p style={{ color: 'var(--red-9, #e5484d)', fontSize: '12px', margin: '4px 0 0', textAlign: 'left' }}>
              {rescheduleError}
            </p>
          )}

          <label style={{ fontSize: '12px', fontWeight: 500, color: 'var(--gray-11)', display: 'block', marginTop: '12px', marginBottom: '4px' }}>Reason (optional)</label>
          <textarea
            value={rescheduleReason}
            onChange={(e) => setRescheduleReason(e.target.value)}
            placeholder="Why are you rescheduling?"
            rows={2}
            style={{
              width: '100%', padding: '10px 12px', borderRadius: '8px',
              border: '1px solid var(--gray-5)', backgroundColor: 'var(--color-background)',
              color: 'var(--gray-12)', fontSize: '13px', resize: 'vertical',
              outline: 'none', fontFamily: 'inherit',
            }}
          />

          <div className="flex gap-2 justify-end" style={{ marginTop: '14px' }}>
            <button
              onClick={() => { setShowRescheduleModal(false); setRescheduleError(''); }}
              disabled={actionLoading}
              style={{
                padding: '6px 16px', borderRadius: '6px', fontSize: '12px', fontWeight: 500,
                border: '1px solid var(--gray-5)', backgroundColor: 'transparent',
                color: 'var(--gray-11)', cursor: 'pointer',
              }}
            >
              Cancel
            </button>
            <button
              onClick={handleRescheduleSubmit}
              disabled={actionLoading}
              style={{
                padding: '6px 16px', borderRadius: '6px', fontSize: '12px', fontWeight: 600,
                border: 'none', backgroundColor: 'var(--orange-9)',
                color: '#fff', cursor: actionLoading ? 'not-allowed' : 'pointer',
                opacity: actionLoading ? 0.6 : 1,
              }}
            >
              {actionLoading ? 'Processing...' : 'Reschedule'}
            </button>
          </div>
        </div>
      </Modal>

      {/* Pay Modal */}
      <Modal
        isOpen={showPayModal}
        onClose={() => { setShowPayModal(false); setPayingBooking(null); setPhoneNumber(''); setPayError(''); }}
        title="Pay for Booking"
      >
        {payingBooking && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            {/* Summary */}
            <div className="rounded-lg p-4" style={{ backgroundColor: 'var(--gray-3)', border: '1px solid var(--gray-4)' }}>
              <div className="flex justify-between mb-2">
                <span style={{ fontSize: '14px', color: 'var(--gray-11)' }}>Service</span>
                <span style={{ fontSize: '14px', fontWeight: 600, color: 'var(--gray-12)' }}>
                  {formatCurrency(payingBooking.subtotal || 0)}
                </span>
              </div>
              <div className="flex justify-between mb-2">
                <span style={{ fontSize: '14px', color: 'var(--gray-11)' }}>Service Fee</span>
                <span style={{ fontSize: '14px', color: 'var(--gray-12)' }}>
                  {formatCurrency(payingBooking.clientFee || 2000)}
                </span>
              </div>
              <div className="flex justify-between pt-2" style={{ borderTop: '1px solid var(--gray-5)' }}>
                <span style={{ fontSize: '16px', fontWeight: 700, color: 'var(--gray-12)' }}>Total</span>
                <span style={{ fontSize: '16px', fontWeight: 700, color: 'var(--orange-9)' }}>
                  {formatCurrency(payingBooking.totalPaid || (payingBooking.subtotal || 0) + (payingBooking.clientFee || 2000))}
                </span>
              </div>
            </div>

            {/* Phone number */}
            <div>
              <label style={{ display: 'block', fontSize: '14px', fontWeight: 500, color: 'var(--gray-12)', marginBottom: '6px', textAlign: 'left' }}>
                MoMo Phone Number <span style={{ color: 'var(--red-9, #e5484d)' }}>*</span>
              </label>
              <input
                type="tel"
                value={phoneNumber}
                onChange={e => {
                  setPhoneNumber(e.target.value);
                  setPayError('');
                }}
                placeholder="e.g. 256771234567"
                style={{
                  width: '100%', padding: '10px 12px', borderRadius: '8px',
                  border: `1px solid ${payError ? 'var(--red-9, #e5484d)' : 'var(--gray-6)'}`, backgroundColor: 'var(--color-background)',
                  color: 'var(--gray-12)', fontSize: '14px', outline: 'none',
                }}
              />
              {payError && (
                <p style={{ fontSize: '12px', color: 'var(--red-9, #e5484d)', marginTop: '4px', textAlign: 'left' }}>
                  {payError}
                </p>
              )}
              <p style={{ fontSize: '12px', color: 'var(--gray-9)', marginTop: '4px', textAlign: 'left' }}>
                Enter the MTN or Airtel number to receive the payment prompt
              </p>
            </div>

            {/* Pay button */}
            <div className="flex gap-3 justify-center">
              <Button
                variant="outline"
                size="small"
                onClick={() => { setShowPayModal(false); setPayingBooking(null); setPhoneNumber(''); setPayError(''); }}
                disabled={payLoading}
              >
                Cancel
              </Button>
              <Button
                size="small"
                loading={payLoading}
                onClick={async () => {
                  if (!phoneNumber || phoneNumber.length < 10) {
                    setPayError('Please enter a valid phone number');
                    return;
                  }
                  setPayLoading(true);
                  try {
                    const result = await paymentsAPI.initiatePayment({
                      bookingId: payingBooking.id,
                      providerId: payingBooking.providerId,
                      amount: payingBooking.totalPaid || (payingBooking.subtotal || 0) + (payingBooking.clientFee || 2000),
                      subtotal: payingBooking.subtotal || 0,
                      clientFee: payingBooking.clientFee || 2000,
                      phoneNumber,
                      method: 'MOBILE_MONEY',
                    });
                    toast.success(result.message || 'Payment initiated! Check your phone.');

                    // Poll for payment completion (sandbox auto-completes)
                    const paymentId = result.paymentId;
                    let attempts = 0;
                    const pollInterval = setInterval(async () => {
                      attempts++;
                      try {
                        const status = await paymentsAPI.getPaymentStatus(paymentId);
                        if (status.status === 'COMPLETED') {
                          clearInterval(pollInterval);
                          toast.success('Payment confirmed!');
                          setShowPayModal(false);
                          setPayingBooking(null);
                          setPhoneNumber('');
                          setPayError('');
                          fetchBookings();
                        } else if (status.status === 'FAILED') {
                          clearInterval(pollInterval);
                          toast.error('Payment failed');
                        }
                      } catch {}
                      if (attempts >= 12) {
                        clearInterval(pollInterval);
                        setShowPayModal(false);
                        setPayingBooking(null);
                        setPhoneNumber('');
                        setPayError('');
                        fetchBookings();
                      }
                    }, 5000); // Check every 5 seconds for up to 60 seconds
                  } catch (error) {
                    console.error('Payment error:', error);
                    toast.error(error.response?.data?.error || 'Payment failed. Please try again.');
                  } finally {
                    setPayLoading(false);
                  }
                }}
                style={{ backgroundColor: '#22c55e', border: 'none' }}
              >
                Pay {formatCurrency(payingBooking.totalPaid || (payingBooking.subtotal || 0) + (payingBooking.clientFee || 2000))}
              </Button>
            </div>

            <p style={{ fontSize: '11px', color: 'var(--gray-9)', textAlign: 'center', margin: 0 }}>
              You will receive a MoMo prompt on your phone to confirm the payment
            </p>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default Bookings;
